import { useCallback, useEffect, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';

import { openChatStream } from '../api/chat.api';
import { isAbortLikeError, readStream } from '../lib/stream';

import type { ContentPart, SendMessageArgs } from '../types/chat.types';

export type UseSendMessageResult = {
  mutation: ReturnType<typeof useMutation<void, unknown, SendMessageArgs>>;
  cancel: () => void;
};

type UseSendMessageCallbacks = {
  onPart: (part: ContentPart) => void;
  onComplete?: () => void;
};

export function useSendMessage({
  onPart,
  onComplete
}: UseSendMessageCallbacks): UseSendMessageResult {
  const abortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  async function streamMessage(params: SendMessageArgs) {
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const reader = await openChatStream(params, controller.signal);
      if (!reader) return;
      const { sawComplete } = await readStream(reader, onPart);
      if (sawComplete && !controller.signal.aborted) {
        setTimeout(() => {
          if (!controller.signal.aborted) onComplete?.();
        }, 0);
      }
    } catch (error) {
      if (error instanceof Error && !isAbortLikeError(error)) {
        onPart({
          kind: 'text',
          text: 'An error occurred while processing the request. Please try again.'
        });
      }
    }
  }

  const mutation = useMutation<void, unknown, SendMessageArgs>({
    mutationFn: streamMessage,
    onSettled: () => {
      abortRef.current = null;
    }
  });

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { mutation, cancel };
}
